import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CoreLangProvider, useCoreT } from '../context/CoreLangContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PhoneOtpForm from '../components/PhoneOtpForm';
import { useEmailAuthEnabled } from '../hooks/useEmailAuthEnabled';

// The business login follows the PUBLIC platform language (same as the homepage),
// not the admin dashboard language — and offers its own switcher. We seed a local
// language from platformLang and drive a nested CoreLangProvider with it so all
// the T.* strings (and the OTP widget) re-render in the chosen language.
export default function BusinessAuthPage() {
  const { platformLang, setPlatformLang, platMode, platEnabled, platformSettings } = useLanguage();
  const [lang, setLang] = useState(platformLang || 'fa');
  useEffect(() => { if (platformLang) setLang(platformLang); }, [platformLang]);
  // Switching here persists to the shared platform language so the homepage and
  // the rest of the app stay in sync.
  const changeLang = (c) => { setLang(c); if (setPlatformLang) setPlatformLang(c); };
  return (
    <CoreLangProvider
      coreMode={lang === 'fa' ? 'persian' : 'global'}
      coreLang={lang}
      coreTranslations={platformSettings?.coreTranslations || {}}
    >
      <BusinessAuthInner
        switcher={{ enabled: platEnabled || [lang], current: lang, onChange: changeLang, show: platMode === 'multi' }}
      />
    </CoreLangProvider>
  );
}

function BusinessAuthInner({ switcher }) {
  const navigate = useNavigate();
  const T = useCoreT();
  const { loginBusinessOwner, registerBusinessOwner, sendOwnerOtp, verifyOwnerOtp } = useAuth();
  const emailAuth = useEmailAuthEnabled() === true; // email login/register only when enabled
  const [mode,    setMode]    = useState('login');
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' | 'email' (phone = priority)
  const [form,    setForm]    = useState({ email: '', password: '', restaurantName: '', ownerName: '', restaurantSlug: '', phone: '' });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Localized labels for the phone-OTP widget (follows the dashboard language).
  const otpLabels = {
    phoneLabel: T.otpPhoneLabel, sendBtn: T.otpSendBtn, codeLabel: T.otpCodeLabel,
    verifyBtn: T.otpVerifyBtn, nameLabel: T.otpNameLabel, namePh: T.otpNamePh,
    changePhone: T.otpChangePhone, resend: T.otpResend, sentTo: T.otpSentTo,
  };

  const handleLogin = async () => {
    setError('');
    if (!form.email || !form.password) { setError(T.bizEmailReq); return; }
    setLoading(true);
    const res = await loginBusinessOwner(form.email, form.password);
    setLoading(false);
    if (res.success) navigate('/business/dashboard');
    else setError(res.error);
  };

  const handleRegister = async () => {
    setError('');
    if (!form.email || !form.password || !form.restaurantName || !form.restaurantSlug || !form.ownerName || !form.phone) {
      setError(T.bizAllFieldsReq); return;
    }
    setLoading(true);
    const res = await registerBusinessOwner({
      ownerName:      form.ownerName,
      email:          form.email,
      password:       form.password,
      phone:          form.phone,
      restaurantSlug: form.restaurantSlug.toLowerCase().replace(/\s+/g, '-'),
      restaurantName: form.restaurantName,
    });
    setLoading(false);
    if (res.success) navigate('/business/dashboard');
    else setError(res.error);
  };

  return (
    <div className="biz-page" dir={T.dir}>
      <div className="biz-left">
        <div className="biz-brand">
          <div className="biz-brand-icon">🌟</div>
          <h1 className="biz-brand-name">Nooshin</h1>
          <p className="biz-brand-tagline">{T.bizTagline}</p>
        </div>
        <ul className="biz-features">
          <li>✅ {T.bizFeatureMenu} (yourname.nowshin.ir)</li>
          <li>✅ {T.bizFeatureOrders}</li>
          <li>✅ {T.bizFeatureProducts}</li>
          <li>✅ {T.bizFeatureInsights}</li>
          <li>✅ {T.bizFeatureTenant}</li>
          <li>✅ {T.bizFeatureMobile}</li>
        </ul>
      </div>

      <div className="biz-right">
        <div className="biz-card">
          {switcher?.show && (
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'.75rem' }}>
              <LanguageSwitcher enabled={switcher.enabled} current={switcher.current} onChange={switcher.onChange} compact={false} />
            </div>
          )}
          {/* With email auth off there is no Register tab, so a lone "Sign In"
              tab is just a dead button — hide the whole tab row. */}
          {emailAuth && (
            <div className="biz-tabs">
              <button className={`biz-tab ${mode === 'login'    ? 'active' : ''}`} onClick={() => setMode('login')}>{T.signIn}</button>
              <button className={`biz-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => setMode('register')}>{T.bizRegisterBusiness}</button>
            </div>
          )}

          {error && <div className="biz-error">{error}</div>}

          {(mode === 'login' || !emailAuth) ? (
            <div className="biz-form">
              {emailAuth && (
                <div className="biz-method">
                  <button className={`biz-method-btn ${loginMethod==='phone'?'active':''}`} onClick={() => { setLoginMethod('phone'); setError(''); }}>{T.bizPhoneMethod}</button>
                  <button className={`biz-method-btn ${loginMethod==='email'?'active':''}`} onClick={() => { setLoginMethod('email'); setError(''); }}>{T.bizEmailMethod}</button>
                </div>
              )}
              {(loginMethod === 'phone' || !emailAuth) ? (
                <PhoneOtpForm
                  accent="#c8a96e"
                  lang={T.lang}
                  labels={otpLabels}
                  askName
                  onSend={sendOwnerOtp}
                  onVerify={async (phone, code, name) => {
                    const res = await verifyOwnerOtp(phone, code, name);
                    if (res.success) navigate('/business/dashboard');
                    return res;
                  }}
                />
              ) : (
                <>
                  <div className="biz-field">
                    <label>{T.bizBusinessEmail}</label>
                    <input className="biz-input" type="email" value={form.email} onChange={update('email')} placeholder="you@yourbusiness.com" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                  </div>
                  <div className="biz-field">
                    <label>{T.password}</label>
                    <input className="biz-input" type="password" value={form.password} onChange={update('password')} placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                  </div>
                  <button className="biz-submit" onClick={handleLogin} disabled={loading}>
                    {loading ? T.signingIn : T.bizSignInDashboard}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="biz-form">
              <div className="biz-field">
                <label>{T.bizRestaurantName} *</label>
                <input className="biz-input" value={form.restaurantName} onChange={update('restaurantName')} placeholder="Yasmin Restaurant" />
              </div>
              <div className="biz-field">
                <label>{T.bizOwnerName} *</label>
                <input className="biz-input" value={form.ownerName} onChange={update('ownerName')} placeholder="Your full name" />
              </div>
              <div className="biz-field">
                <label>{T.bizMenuSubdomain} * <span className="biz-hint-inline">(yourname.nowshin.ir)</span></label>
                <div className="biz-slug-wrap">
                  <input className="biz-input biz-slug-input" value={form.restaurantSlug} onChange={update('restaurantSlug')} placeholder="yourname" />
                  <span className="biz-slug-suffix">.nowshin.ir</span>
                </div>
              </div>
              <div className="biz-field">
                <label>{T.bizBusinessEmail} *</label>
                <input className="biz-input" type="email" value={form.email} onChange={update('email')} placeholder="you@business.com" />
              </div>
              <div className="biz-field">
                <label>{T.phone} * <span className="biz-hint-inline">({T.bizUsedForSms})</span></label>
                <input className="biz-input" value={form.phone} onChange={update('phone')} placeholder="09xx xxx xxxx" />
              </div>
              <div className="biz-field">
                <label>{T.password} *</label>
                <input className="biz-input" type="password" value={form.password} onChange={update('password')} placeholder={T.bizCreateStrongPass} />
              </div>
              <button className="biz-submit" onClick={handleRegister} disabled={loading}>
                {loading ? T.bizRegistering : T.bizRegisterMyBiz}
              </button>
              <p className="biz-terms">{T.bizTerms}</p>
            </div>
          )}

          <div className="biz-footer-links">
            <button className="biz-link" onClick={() => navigate("/")}>{T.bizBackToHome}</button>
            <button className="biz-link" onClick={() => navigate('/admin/login')}>{T.adminLogin}</button>
          </div>
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0a0a0a; }
        .biz-page { min-height: 100vh; display: grid; grid-template-columns: 1fr 1fr; font-family: 'DM Sans', sans-serif; }
        @media (max-width: 768px) { .biz-page { grid-template-columns: 1fr; } .biz-left { display: none; } }
        .biz-left { background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%); display: flex; flex-direction: column; justify-content: center; padding: 4rem; }
        .biz-brand { margin-bottom: 3rem; }
        .biz-brand-icon { font-size: 3rem; margin-bottom: 1rem; }
        .biz-brand-name { font-family: 'Playfair Display', serif; font-size: 3rem; font-weight: 900; color: #fff; margin-bottom: 0.75rem; }
        .biz-brand-tagline { color: rgba(255,255,255,0.6); font-size: 1.1rem; line-height: 1.6; }
        .biz-features { list-style: none; display: flex; flex-direction: column; gap: 1rem; }
        .biz-features li { color: rgba(255,255,255,0.75); font-size: 0.95rem; line-height: 1.5; }
        .biz-demo-hint { margin-top: 2.5rem; padding: 1rem 1.5rem; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; color: rgba(255,255,255,0.5); font-size: 0.85rem; }
        .biz-demo-hint strong { color: rgba(255,255,255,0.8); }
        .biz-right { background: #0f0f0f; display: flex; align-items: center; justify-content: center; padding: 2rem; }
        .biz-card { width: 100%; max-width: 460px; }
        .biz-tabs { display: flex; background: rgba(255,255,255,0.04); border-radius: 12px; padding: 0.3rem; margin-bottom: 2rem; border: 1px solid rgba(255,255,255,0.08); }
        .biz-tab { flex: 1; padding: 0.7rem; background: none; border: none; color: rgba(255,255,255,0.45); cursor: pointer; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 500; transition: all 0.2s; }
        .biz-tab.active { background: #c8a96e; color: #fff; font-weight: 700; }
        .biz-method { display: flex; gap: 0.5rem; margin-bottom: 1.25rem; }
        .biz-method-btn { flex: 1; padding: 0.6rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: rgba(255,255,255,0.55); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; }
        .biz-method-btn.active { background: #c8a96e; border-color: #c8a96e; color: #fff; }
        .biz-error { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); border-radius: 10px; padding: 0.75rem 1rem; color: #fca5a5; font-size: 0.85rem; margin-bottom: 1.5rem; }
        .biz-form { display: flex; flex-direction: column; gap: 1rem; }
        .biz-field { display: flex; flex-direction: column; gap: 0.45rem; }
        .biz-field label { font-size: 0.75rem; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.08em; }
        .biz-hint-inline { font-size: 0.65rem; color: rgba(255,255,255,0.25); text-transform: none; letter-spacing: 0; }
        .biz-input { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 0.8rem 1rem; color: #fff; font-family: 'DM Sans', sans-serif; font-size: 0.9rem; outline: none; transition: border-color 0.2s; width: 100%; }
        .biz-input:focus { border-color: #c8a96e; }
        .biz-slug-wrap { display: flex; align-items: center; border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; overflow: hidden; transition: border-color 0.2s; background: rgba(255,255,255,0.06); }
        .biz-slug-wrap:focus-within { border-color: #c8a96e; }
        .biz-slug-input { border: none; border-radius: 0; background: transparent; flex: 1; }
        .biz-slug-suffix { padding: 0 1rem; color: rgba(255,255,255,0.3); font-size: 0.85rem; white-space: nowrap; border-left: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.03); height: 44px; display: flex; align-items: center; }
        .biz-submit { margin-top: 0.5rem; padding: 1rem; background: linear-gradient(135deg, #c8a96e, #a0845a); border: none; border-radius: 12px; color: #fff; font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s, transform 0.2s; }
        .biz-submit:hover:not(:disabled) { opacity: 0.9; transform: scale(1.01); }
        .biz-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .biz-terms { font-size: 0.72rem; color: rgba(255,255,255,0.25); text-align: center; line-height: 1.5; }
        .biz-footer-links { display: flex; justify-content: space-between; margin-top: 1.5rem; }
        .biz-link { background: none; border: none; color: rgba(255,255,255,0.3); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 0.8rem; transition: color 0.2s; }
        .biz-link:hover { color: rgba(255,255,255,0.7); }
      `}</style>
    </div>
  );
}
